import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "role", label: "Role" },
  { v: "allow", label: "Allow" },
  { v: "deny", label: "Deny" },
];

const ModeToggle = ({ value, onChange, testId }) => (
  <div className="inline-flex rounded-md border border-border overflow-hidden text-xs shrink-0" data-testid={testId}>
    {MODES.map((m) => (
      <button key={m.v} type="button" data-testid={`${testId}-${m.v}`} onClick={() => onChange(m.v)}
        className={`px-2 py-1 transition-colors ${value === m.v ? (m.v === "allow" ? "bg-emerald-600 text-white" : m.v === "deny" ? "bg-rose-600 text-white" : "bg-muted text-foreground font-medium") : "text-muted-foreground hover:bg-muted/60"}`}>
        {m.label}
      </button>
    ))}
  </div>
);

export const UserPermissionsDialog = ({ target, role, groups, onClose, onSaved }) => {
  const [allow, setAllow] = useState([]);
  const [deny, setDeny] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) { setAllow(target.perm_allow || []); setDeny(target.perm_deny || []); } }, [target]);

  const rolePerms = role?.permissions || [];
  const fromRole = (k) => rolePerms.includes("*") || rolePerms.includes(k);
  const fullAccess = allow.includes("*");
  const modeOf = (k) => (allow.includes(k) ? "allow" : deny.includes(k) ? "deny" : "role");
  const setMode = (k, m) => {
    setAllow((a) => (m === "allow" ? [...a.filter((x) => x !== k), k] : a.filter((x) => x !== k)));
    setDeny((d) => (m === "deny" ? [...d.filter((x) => x !== k), k] : d.filter((x) => x !== k)));
  };
  const effective = (k) => (deny.includes(k) ? false : fullAccess || allow.includes(k) || fromRole(k));
  const allKeys = groups.flatMap((g) => g.permissions.map((p) => p.key));
  const count = allKeys.filter(effective).length;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${target.id}/permissions`, { allow, deny });
      toast.success(`Permissions saved for ${target.name}`);
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-perms-dialog">
        <DialogHeader>
          <DialogTitle>Custom permissions — {target?.name}</DialogTitle>
          <DialogDescription>
            Role: <span className="font-medium text-foreground">{role?.name || "—"}</span>. "Role" uses the role's setting; "Allow" or "Deny" overrides it for this person only.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
          <div><Label>Full access (Super Admin)</Label><div className="text-xs text-muted-foreground">Grants every permission, including the Danger Zone reset.</div></div>
          <Switch checked={fullAccess} data-testid="perm-full-access-switch"
            onCheckedChange={(v) => setAllow((a) => (v ? [...a.filter((x) => x !== "*"), "*"] : a.filter((x) => x !== "*")))} />
        </div>
        <div className="text-xs text-muted-foreground" data-testid="perm-effective-count">{count} of {allKeys.length} permissions effective · {allow.filter((x) => x !== "*").length} allowed · {deny.length} denied</div>
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.group}>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{g.group}</div>
              <div className="space-y-1">
                {g.permissions.map((p) => (
                  <div key={p.key} className="flex items-center justify-between gap-3 py-1">
                    <div className="flex items-center gap-2 min-w-0 text-sm">
                      {effective(p.key) ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <X className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <span className="truncate">{p.label}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0">role: {fromRole(p.key) ? "on" : "off"}</span>
                    </div>
                    <ModeToggle value={modeOf(p.key)} onChange={(m) => setMode(p.key, m)} testId={`perm-mode-${p.key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setAllow([]); setDeny([]); }} data-testid="perm-reset-btn">Reset to role</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-user-perms-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
